import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { useDialog } from '../../shared/hooks/useDialog';
import { deleteGroup } from '../helpers/groupsApi';
import { leaveGroup, removeGroupMember } from '../helpers/groupMembersApi';
import type { GroupLeaderboardEntry } from '../helpers/groupLeaderboard.helper';

interface GroupForMemberActions {
  id: string;
  name?: string;
  memberIds?: string[];
}

interface UseGroupMemberActionsResult {
  handleLeaveGroup: () => Promise<void>;
  handleRemoveMember: (entry: GroupLeaderboardEntry) => Promise<void>;
  isRemoveMemberBusy: boolean;
}

export function useGroupMemberActions(
  group: GroupForMemberActions | null,
): UseGroupMemberActionsResult {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const router = useRouter();
  const { showDialog, showAlert } = useDialog();
  const [isRemoveMemberBusy, setIsRemoveMemberBusy] = useState(false);
  const removeLockRef = useRef(false);

  const handleLeaveGroup = useCallback(async () => {
    if (!group) return;
    const groupName = group.name ?? 'this group';
    const isLastMember = (group.memberIds?.length ?? 0) <= 1;
    const confirmed = await showDialog({
      title: isLastMember ? 'Delete group' : 'Leave group',
      message: isLastMember
        ? `You're the last member. Leave and delete "${groupName}"?`
        : `Are you sure you want to leave "${groupName}"?`,
      type: 'danger',
      confirmText: isLastMember ? 'Delete' : 'Leave',
      cancelText: 'Cancel',
    });
    if (!confirmed) return;
    try {
      if (isLastMember) {
        await deleteGroup(group.id, apiClient);
      } else {
        await leaveGroup(group.id, apiClient);
      }
      queryClient.removeQueries({ queryKey: ['groups', 'dashboard', group.id] });
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      router.replace('/app/friends?tab=groups');
    } catch {
      await showAlert({
        title: 'Error',
        message: isLastMember
          ? 'Could not delete the group.'
          : 'Could not leave the group. Try again.',
        type: 'danger',
      });
    }
  }, [group, apiClient, queryClient, router, showDialog, showAlert]);

  const handleRemoveMember = useCallback(
    async (entry: GroupLeaderboardEntry) => {
      if (!group) return;
      const confirmed = await showDialog({
        title: 'Remove from group',
        message: `Remove ${entry.name} from "${group.name ?? 'this group'}"?`,
        type: 'danger',
        confirmText: 'Remove',
        cancelText: 'Cancel',
      });
      if (!confirmed) return;
      if (removeLockRef.current) return;
      removeLockRef.current = true;
      setIsRemoveMemberBusy(true);
      try {
        await removeGroupMember(group.id, entry.userId, apiClient);
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['groups'] }),
          queryClient.invalidateQueries({ queryKey: ['groups', 'dashboard', group.id] }),
        ]);
        await queryClient.refetchQueries({ queryKey: ['groups', 'dashboard', group.id] });
        await showAlert({
          title: 'Removed successfully',
          message: `${entry.name} has been removed from the group.`,
        });
      } catch {
        await showAlert({
          title: 'Error',
          message: 'Could not remove member. Try again.',
          type: 'danger',
        });
      } finally {
        setIsRemoveMemberBusy(false);
        removeLockRef.current = false;
      }
    },
    [group, apiClient, queryClient, showDialog, showAlert],
  );

  return { handleLeaveGroup, handleRemoveMember, isRemoveMemberBusy };
}
